import { useEffect, useRef, useState } from 'react'

import { useCheckInternetConnection } from '@/hooks'
import { useTranslate } from '@/i18n/useTranslate'
import { getAgeVerificationDebugOverlayEnabled } from '@/services/deviceConfigurationService'
import {
    type IcuCallResult,
    subscribeIcuResponses
} from '@/services/icuVerificationService'

const TOAST_DURATION_MS = 10000

type Toast = IcuCallResult & { id: number }

export default function IcuDebugOverlay() {
    const { deviceConfiguration } = useCheckInternetConnection()
    return getAgeVerificationDebugOverlayEnabled(deviceConfiguration) ? (
        <IcuDebugToasts />
    ) : null
}

// Mounted outside the route so activation, verification and cleanup responses
// remain visible through automatic navigation. Disabling the flag clears all toasts.
function IcuDebugToasts() {
    const { t } = useTranslate()
    const [toasts, setToasts] = useState<Toast[]>([])
    const nextId = useRef(0)

    useEffect(() => {
        const timers = new Set<ReturnType<typeof setTimeout>>()
        const unsubscribe = subscribeIcuResponses((result) => {
            const id = ++nextId.current
            setToasts((current) => [...current, { ...result, id }])
            const timer = setTimeout(() => {
                setToasts((current) =>
                    current.filter((toast) => toast.id !== id)
                )
                timers.delete(timer)
            }, TOAST_DURATION_MS)
            timers.add(timer)
        })
        return () => {
            unsubscribe()
            for (const timer of timers) clearTimeout(timer)
        }
    }, [])

    return (
        <div
            className="icu-debug-overlay"
            role="log"
            aria-live="polite"
            aria-label="ICU diagnostics">
            {toasts.map((toast) => (
                <div
                    key={toast.id}
                    className={`icu-debug-overlay__toast icu-debug-overlay__toast--${toast.status}`}>
                    <strong>
                        ICU {toast.operation} —{' '}
                        {t(`icuDebugOverlay.${toast.status}`)}
                    </strong>
                    <p>
                        {toast.status === 'success'
                            ? toast.age !== undefined
                                ? t('icuDebugOverlay.age', { age: toast.age })
                                : t('icuDebugOverlay.noAge')
                            : t('icuDebugOverlay.reason', {
                                  reason: toast.reason
                              })}
                    </p>
                </div>
            ))}
        </div>
    )
}
